/* ===== Profile Page (personal profile + security, all roles) ===== */

import { useEffect, useRef, useState } from 'react'
import { Lock, Save, User } from 'lucide-react'
import { PLATFORM_ORGANIZATION_ID } from '@zetyar/data-contracts'
import { useApp } from '@/presentation/providers/useApp'
import { Card, CardContent, CardHeader, CardTitle } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Avatar } from '@/presentation/components/ui/Avatar'
import { uploadImageAsset } from '@/infrastructure/storage/images'
import { getSupabaseClient } from '@/infrastructure/supabase/app'
import { useReauthGuard } from '@/presentation/hooks/useReauthGuard'

export function ProfilePage() {
  const { profile, currentOrganization, updateProfile } = useApp()
  const { guard, reauthModal } = useReauthGuard()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [displayName, setDisplayName] = useState(profile?.displayName || '')
  const [phone, setPhone] = useState(profile?.phone || '')
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatarUrl || '')
  const [rut, setRut] = useState(profile?.rut || '')
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.displayName || '')
      setPhone(profile.phone || '')
      setAvatarUrl(profile.avatarUrl || '')
      setRut(profile.rut || '')
    }
  }, [profile])

  const handleUpdateAvatar = (e: React.MouseEvent) => {
    e.preventDefault()
    avatarInputRef.current?.click()
  }

  const handleAvatarFile = async (file: File | undefined) => {
    if (!file || !profile) return
    try {
      setIsUploadingAvatar(true)
      const uploadedUrl = await uploadImageAsset({
        organizationId: currentOrganization?.id ?? PLATFORM_ORGANIZATION_ID,
        scope: 'profile',
        ownerId: profile.id,
        file,
        label: 'avatar',
        maxBytes: 2 * 1024 * 1024,
      })
      setAvatarUrl(uploadedUrl)
      await updateProfile(displayName, phone || null, uploadedUrl, rut || null)
    } catch (error) {
      console.error('Error al subir avatar:', error)
      alert(error instanceof Error ? error.message : 'No se pudo subir la imagen.')
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateProfile(displayName, phone || null, avatarUrl || null, rut || null)
      alert('Perfil actualizado con éxito.')
    } catch {
      alert('Error al actualizar el perfil.')
    }
  }

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordError(null)

    if (newPassword.length < 6) {
      setPasswordError('La contrasena debe tener al menos 6 caracteres.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contrasenas no coinciden.')
      return
    }

    guard(async () => {
      setIsChangingPassword(true)
      try {
        const client = getSupabaseClient()
        const { error } = await client.auth.updateUser({ password: newPassword })
        if (error) throw error
        setNewPassword('')
        setConfirmPassword('')
        alert('Contrasena actualizada con exito.')
      } catch (error) {
        console.error('Error al cambiar la contrasena:', error)
        setPasswordError('No se pudo actualizar la contrasena.')
      } finally {
        setIsChangingPassword(false)
      }
    })
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Mi perfil</h1>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
          Administra tu informacion personal y la seguridad de tu cuenta.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-5 h-5 text-primary-600" /> Mi perfil
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="flex items-center gap-4 mb-4">
              <Avatar src={avatarUrl} name={displayName} size="xl" />
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => void handleAvatarFile(event.target.files?.[0])}
              />
              <div>
                <Button variant="outline" size="sm" onClick={handleUpdateAvatar} isLoading={isUploadingAvatar}>
                  Cambiar fotografia
                </Button>
                <p className="text-xs text-neutral-400 mt-1">JPG, PNG o WebP. Maximo 2 MB.</p>
              </div>
            </div>

            <Input
              label="Nombre completo"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <Input label="Correo electronico" value={profile?.email} disabled helperText="Correo verificado" />
            <Input
              label="Telefono de contacto"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+569..."
            />
            <Input
              label="RUT (Identificación)"
              value={rut}
              onChange={(e) => setRut(e.target.value)}
              placeholder="12.345.678-9"
            />

            <div className="pt-2 flex justify-end">
              <Button type="submit" variant="primary" leftIcon={<Save className="w-4 h-4" />}>
                Guardar perfil
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary-600" /> Seguridad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <Input
              label="Nueva contrasena"
              type="password"
              minLength={6}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimo 6 caracteres"
            />
            <Input
              label="Confirmar nueva contrasena"
              type="password"
              minLength={6}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {passwordError && <p className="text-xs text-danger-600">{passwordError}</p>}

            <div className="pt-2 flex justify-end">
              <Button type="submit" variant="primary" leftIcon={<Lock className="w-4 h-4" />} isLoading={isChangingPassword}>
                Cambiar contrasena
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {reauthModal}
    </div>
  )
}
